/**
 * Navigation Guide Component - Feature Discovery Tool
 * Version: 6.2.0
 * 
 * Now connected to backend Feature Registry API
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchNavigationItems,
  fetchCategories,
  searchFeatures,
  addToRecentFeatures,
  getRecentFeatures,
  type Feature,
  type FeatureCategory,
} from '../api/features'
import './NavigationGuide.css'

// Default fallback items if API fails
const DEFAULT_NAVIGATION_ITEMS: Partial<Feature>[] = [
  // Dashboard
  { id: 1, name: 'Neural Dashboard', icon: '🧠', page_url: '/', feature_type: 'page', keywords: 'home,dashboard,overview,stats' },
  { id: 2, name: 'System Status', icon: '🔮', page_url: '/#status', feature_type: 'section', keywords: 'health,system,status' },
  { id: 3, name: 'Quick Actions', icon: '⚡', page_url: '/#actions', feature_type: 'section', keywords: 'actions,quick' },
  
  // Brokers
  { id: 4, name: 'Broker Connect', icon: '🔗', page_url: '/brokers', feature_type: 'page', keywords: 'broker,connect,trading' },
  { id: 5, name: 'Major Brokers', icon: '⭐', page_url: '/brokers#major', feature_type: 'section', keywords: 'zerodha,angel,upstox,fyers' },
  
  // Strategies
  { id: 6, name: 'Strategy Lab', icon: '⚙️', page_url: '/strategies', feature_type: 'page', keywords: 'strategy,trading,build' },
  { id: 7, name: 'Pre-built Strategies', icon: '🚀', page_url: '/strategies#prebuilt', feature_type: 'section', keywords: 'prebuilt,ready' },
  { id: 8, name: 'Backtesting', icon: '📊', page_url: '/strategies#backtest', feature_type: 'section', keywords: 'backtest,test,history' },
  { id: 9, name: 'AI Strategy Builder', icon: '🧠', page_url: '/strategies#builder', feature_type: 'section', keywords: 'ai,builder,create' },
  
  // Exchanges
  { id: 10, name: 'Crypto Exchanges', icon: '🌐', page_url: '/#exchanges', feature_type: 'section', keywords: 'crypto,binance,bybit' },
  
  // Analytics
  { id: 11, name: 'Analytics', icon: '📈', page_url: '/analytics', feature_type: 'page', keywords: 'analytics,reports,performance' },
  { id: 12, name: 'Portfolio', icon: '💼', page_url: '/portfolio', feature_type: 'page', keywords: 'portfolio,holdings' },
  
  // Settings
  { id: 13, name: 'Settings', icon: '⚙️', page_url: '/#settings', feature_type: 'page', keywords: 'settings,config' },
  { id: 14, name: 'Help & Docs', icon: '📚', page_url: '/#help', feature_type: 'page', keywords: 'help,docs,support' },
]

interface NavigationGuideProps {
  isOpen: boolean
  onClose: () => void
}

function NavigationGuide({ isOpen, onClose }: NavigationGuideProps) {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [features, setFeatures] = useState<Partial<Feature>[]>(DEFAULT_NAVIGATION_ITEMS)
  const [categories, setCategories] = useState<FeatureCategory[]>([])
  const [recentFeatures, setRecentFeatures] = useState<Partial<Feature>[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showRecent, setShowRecent] = useState(true)
  const [retryCount, setRetryCount] = useState(0)

  // Fetch features from API
  const loadFeatures = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const [navItems, cats] = await Promise.all([
        fetchNavigationItems(),
        fetchCategories(),
      ])
      
      if (navItems.length > 0) {
        setFeatures(navItems)
      }
      
      if (cats.length > 0) {
        setCategories(cats)
      }
      
      setRetryCount(0) // Reset retry count on success
    } catch (err) {
      console.warn('Failed to fetch features from API, using defaults:', err)
      setError('Using offline mode')
      // Keep using default items
    } finally {
      setLoading(false)
    }
  }, [])

  // Retry loading features
  const handleRetry = () => {
    setRetryCount((prev) => prev + 1)
    loadFeatures()
  }

  // Load recent features from local storage
  const loadRecentFeatures = useCallback(() => {
    const recent = getRecentFeatures()
    setRecentFeatures(recent)
  }, [])

  // Initial load
  useEffect(() => {
    if (isOpen) {
      loadFeatures()
      loadRecentFeatures()
    }
  }, [isOpen, loadFeatures, loadRecentFeatures])

  // Filter features based on search and category
  const filteredFeatures = features.filter((feature) => {
    const matchesSearch =
      searchTerm === '' ||
      feature.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.keywords?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.tags?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory =
      selectedCategory === null ||
      feature.feature_type === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  // Update active index when filtered results change
  useEffect(() => {
    setActiveIndex(0)
  }, [searchTerm, selectedCategory])

  // Get unique categories from features
  const featureCategories = [...new Set(features.map((f) => f.feature_type || 'other'))]

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, filteredFeatures.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && filteredFeatures[activeIndex]) {
        e.preventDefault()
        handleSelectFeature(filteredFeatures[activeIndex])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, activeIndex, filteredFeatures])

  // Handle feature selection
  const handleSelectFeature = (feature: Partial<Feature>) => {
    if (feature.page_url) {
      // Add to recent
      if (feature.id) {
        addToRecentFeatures(feature as Feature)
        loadRecentFeatures()
      }
      
      // Navigate
      navigate(feature.page_url)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="nav-guide-overlay" onClick={onClose}>
      <div className="nav-guide" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="nav-guide-header">
          <div className="nav-guide-title">
            <span className="nav-guide-icon">🗺️</span>
            <span>Feature Search</span>
            {loading && <span className="loading-spinner" />}
            {error && (
              <span className="offline-badge" title={error}>
                ⚡ Offline
              </span>
            )}
          </div>
          <div className="header-actions">
            {error && retryCount < 3 && (
              <button className="retry-btn" onClick={handleRetry} title="Retry loading features">
                🔄 Retry
              </button>
            )}
            <button className="nav-guide-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Search */}
        <div className="nav-guide-search">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search features... (e.g., 'Connect Broker', 'Backtest')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <span className="search-hint">↑↓ navigate • Enter select • Esc close</span>
        </div>

        {/* Recent Features (shown when no search) */}
        {showRecent && !searchTerm && recentFeatures.length > 0 && (
          <div className="recent-features">
            <div className="recent-header">
              <span>Recent</span>
              <button className="clear-recent" onClick={() => { setRecentFeatures([]); localStorage.removeItem('zobull_recent_features'); }}>
                Clear
              </button>
            </div>
            <div className="recent-list">
              {recentFeatures.slice(0, 5).map((feature, index) => (
                <button
                  key={`recent-${feature.id}`}
                  className="recent-item"
                  onClick={() => handleSelectFeature(feature)}
                >
                  <span className="item-icon">{feature.icon || '📄'}</span>
                  <span className="item-label">{feature.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="nav-guide-categories">
          <button
            className={`category-btn ${selectedCategory === null ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All
          </button>
          {featureCategories.map((cat) => (
            <button
              key={cat}
              className={`category-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'page' ? '📄 Pages' : cat === 'section' ? '📑 Sections' : cat}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="nav-guide-results">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner large" />
              <p>Loading features...</p>
            </div>
          ) : filteredFeatures.length === 0 ? (
            <div className="no-results">
              <span>🔍</span>
              <p>No features found for "{searchTerm}"</p>
              <p className="hint">Try: "broker", "strategy", "backtest", "settings"</p>
            </div>
          ) : (
            filteredFeatures.map((feature, index) => (
              <button
                key={feature.id || index}
                className={`nav-guide-item ${index === activeIndex ? 'active' : ''}`}
                onClick={() => handleSelectFeature(feature)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="item-icon">{feature.icon || '📄'}</span>
                <div className="item-content">
                  <span className="item-label">
                    {feature.name}
                    {feature.is_new && <span className="new-badge">New</span>}
                  </span>
                  <span className="item-desc">{feature.description || feature.keywords}</span>
                </div>
                <span className="item-category">{feature.feature_type}</span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="nav-guide-footer">
          <span className="shortcut-hint">
            <kbd>Ctrl</kbd>+<kbd>K</kbd> to open
          </span>
          <span className="results-count">
            {filteredFeatures.length} features
          </span>
        </div>
      </div>
    </div>
  )
}

export default NavigationGuide